import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Layout } from "../layout/Layout";
import { login } from "../core/auth/auth";
import { ModalResendVerification } from "../components/modals/ModalResendVerification";
import { clearSession, type ServixUserType } from "../core/session/session";
import Loader from "./loader";

type LoginMode = "general" | "admin";

type LoginProps = {
	mode?: LoginMode;
};

function isSuspendedLoginError(message: string | null): boolean {
	if (!message) return false;
	if (/\b(motivo|levantamiento)\s*:/i.test(message)) return true;
	const normalized = message.toLowerCase();
	if (normalized.includes("eliminada permanentemente") || normalized.includes("eliminado permanentemente")) return true;
	return /suspendid[oa]s?|suspensi[oó]n|sancionad[oa]s?|bloquead[oa]\s+por\s+sanci[oó]n|banead[oa]s?|ban/i.test(
		normalized,
	);
}
/*hola*/
function normalizeSuspensionField(value: string | null | undefined): string | null {
	if (!value) return null;
	const cleaned = value
		.trim()
		.replace(/^[\s:;,.-]+/, "")
		.replace(/[\s:;,.-]+$/, "");
	return cleaned.length > 0 ? cleaned : null;
}

function isUnverifiedProviderLoginError(message: string | null): boolean {
	if (!message) return false;
	const normalized = message.toLowerCase();
	return (
		normalized.includes("proveedor no verificado") ||
		normalized.includes("proveedor no verificada") ||
		normalized.includes("cuenta de proveedor no verificada") ||
		normalized.includes("cuenta de proveedor no verificado")
	);
}

function isUnverifiedEmailLoginError(message: string | null): boolean {
	if (!message) return false;
	const normalized = message.toLowerCase();
	return (
		normalized.includes("correo no verificado") ||
		normalized.includes("correo electrónico no verificado") ||
		normalized.includes("correo electronico no verificado") ||
		normalized.includes("email no verificado")
	);
}

function getSuspensionDetails(message: string | null): {
	reason: string | null;
	unbanDate: string | null;
} {
	if (!message) {
		return { reason: null, unbanDate: null };
	}

	const reasonMatch = message.match(/motivo:\s*(.*?)(?:\.|$)/i);
	const unbanMatch = message.match(/levantamiento:\s*(.*?)(?:\.|$)/i);
	const fallbackReasonMatch = message.match(/^(.*?)(?:\.\s*levantamiento:|$)/i);
	const fallbackReason = fallbackReasonMatch?.[1]?.trim() || null;
	const normalizedFallbackReason =
		fallbackReason &&
		!/^usuario\s+suspendido$/i.test(fallbackReason) &&
		fallbackReason.toLowerCase() !== "credenciales invalidas"
			? fallbackReason
			: null;

	return {
		reason:
			normalizeSuspensionField(reasonMatch?.[1]) ||
			normalizeSuspensionField(normalizedFallbackReason),
		unbanDate: normalizeSuspensionField(unbanMatch?.[1]),
	};
}

function isPermanentlyDeletedError(message: string | null): boolean {
	if (!message) return false;
	const normalized = message.toLowerCase();
	return normalized.includes("eliminada permanentemente") || normalized.includes("eliminado permanentemente");
}

function getAccountWarning(
	message: string | null,
):
	| { kind: "none" }
	| { kind: "verification" }
	| { kind: "deleted" }
	| { kind: "suspension"; reason: string | null; unbanDate: string | null }
	| { kind: "generic" } {
	if (!message) return { kind: "none" };

	if (isUnverifiedEmailLoginError(message)) {
		return { kind: "generic" };
	}

	if (isUnverifiedProviderLoginError(message)) {
		return { kind: "verification" };
	}

	if (isPermanentlyDeletedError(message)) {
		return { kind: "deleted" };
	}

	if (isSuspendedLoginError(message)) {
		const suspensionDetails = getSuspensionDetails(message);
		return {
			kind: "suspension",
			reason: suspensionDetails.reason,
			unbanDate: suspensionDetails.unbanDate,
		};
	}

	return { kind: "generic" };
}

const GENERAL_ALLOWED_ROLES: readonly ServixUserType[] = ["Cliente", "Proveedor"];
const ADMIN_ALLOWED_ROLES: readonly ServixUserType[] = ["Gerente", "Soporte"];

function getRoleHome(role: ServixUserType | null): string {
	if (role === "Gerente") return "/admin/dashboard";
	if (role === "Soporte") return "/admin/dashboard";
	if (role === "Proveedor") return "/service/profile";
	if (role === "Cliente") return "/profile";
	return "/";
}

function getUnauthorizedRoleMessage(mode: LoginMode): string {
	if (mode === "admin") {
		return "Este acceso es exclusivo para Gerente y Soporte.";
	}
	return "En este login solo pueden ingresar Clientes y Proveedores.";
}

export function Login({ mode = "general" }: LoginProps) {
	const navigate = useNavigate();
	const location = useLocation() as {
		state?: { redirectTo?: string; redirectState?: unknown };
	};
	const isAdminMode = mode === "admin";
	const allowedRoles = isAdminMode ? ADMIN_ALLOWED_ROLES : GENERAL_ALLOWED_ROLES;
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showResendModal, setShowResendModal] = useState(false);
	const isUnverifiedEmailError = isUnverifiedEmailLoginError(error);
	const accountWarning = getAccountWarning(error);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsSubmitting(true);

		try {
			const normalizedEmail = email.trim().toLowerCase();
			const session = await login({ email: normalizedEmail, password });
			const sessionRole = session.user?.type_user ?? null;

			if (!sessionRole || !allowedRoles.includes(sessionRole)) {
				clearSession();
				setError(getUnauthorizedRoleMessage(mode));
				return;
			}

			if (!isAdminMode && sessionRole === "Cliente") {
				navigate("/", { replace: true });
				return;
			}

			if (!isAdminMode && sessionRole === "Proveedor") {
				navigate(getRoleHome(sessionRole), { replace: true });
				return;
			}

			const redirectTo = location.state?.redirectTo;
			if (redirectTo) {
				navigate(redirectTo, {
					replace: true,
					state: location.state?.redirectState,
				});
				return;
			}

			navigate(getRoleHome(sessionRole), { replace: true });
		} catch (err) {
			const raw = err instanceof Error ? err.message : "No se pudo iniciar sesión";
			setError(raw);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Layout>
			<div className='bg-white px-4 py-10'>
				<div className='mx-auto flex min-h-[calc(100vh-120px)] w-full items-center justify-center'>
					<div className='w-full max-w-md rounded-2xl bg-white px-10 py-10 shadow-[0_16px_40px_rgba(15,23,42,0.12)] flex flex-col justify-center'>
						<h1 className='text-center text-xl font-medium text-slate-700'>
							{isAdminMode ? "Acceso Administrativo" : "Iniciar Sesión"}
						</h1>
						{isSubmitting ? (
							<Loader />
						) : (
							<form onSubmit={handleSubmit} className='mt-7 space-y-5'>
								{error ? (
									<div
										className={`rounded-xl px-4 py-3 text-sm ${
											accountWarning.kind === "verification"
												? "border border-sky-200 bg-sky-50 text-sky-900"
												: "border border-rose-200 bg-rose-50 text-rose-700"
										}`}
									>
										{isUnverifiedEmailError ? (
											<div className='space-y-2'>
												<p className='font-semibold'>
													Tu correo aún no está verificado.
												</p>
												<p className='text-rose-700'>
													Presiona en "Verificar cuenta" en la parte inferior para reenviar la verificación.
												</p>
											</div>
										) : accountWarning.kind === "verification" ? (
											<div className='space-y-2'>
												<p className='font-semibold'>
													Tu cuenta de proveedor aún no está
													verificada.
												</p>
												<p>
													Proveedor no verificado. Contacta a
													soporte para continuar con el acceso.
												</p>
											</div>
										) : accountWarning.kind === "deleted" ? (
											<div className='space-y-2'>
												<p className='font-semibold text-rose-800'>
													Esta cuenta ha sido eliminada permanentemente.
												</p>
												<p className='text-rose-700'>
													No es posible iniciar sesión. Si consideras que es un error, contacta a soporte.
												</p>
											</div>
										) : accountWarning.kind === "suspension" ? (
											<div className='space-y-2'>
												<p className='font-semibold text-rose-800'>
													Tu cuenta se encuentra suspendida.
												</p>
												<p className='text-rose-700'>
													No es posible iniciar sesión en este
													momento.
												</p>
												{accountWarning.reason ? (
													<p className='text-rose-700'>
														<span className='font-semibold text-rose-800'>
															Motivo:
														</span>{" "}
														{accountWarning.reason}
													</p>
												) : null}
												{accountWarning.unbanDate ? (
													<p className='text-rose-700'>
														<span className='font-semibold text-rose-800'>
															Levantamiento:
														</span>{" "}
														{accountWarning.unbanDate}
													</p>
												) : null}
												<p className='pt-1 text-rose-700'>
													Si consideras que es un error,
													contacta a soporte.
												</p>
											</div>
										) : (
											error
										)}
									</div>
								) : null}
								<div className='space-y-2'>
									<label
										htmlFor='email'
										className='block text-xs font-medium text-slate-500'
									>
										Correo electrónico
									</label>
									<input
										id='email'
										name='email'
										type='email'
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder='tu@correo.com'
										className='w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										autoComplete='email'
										aria-label='Correo electrónico'
										required
									/>
								</div>

								<div className='space-y-2'>
									<label
										htmlFor='password'
										className='block text-xs font-medium text-slate-500'
									>
										Contraseña
									</label>
									<div className='relative'>
										<input
											id='password'
											name='password'
											type={showPassword ? "text" : "password"}
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											placeholder='••••••••'
											className='w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-10 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
											autoComplete='current-password'
											aria-label='Contraseña'
											required
										/>
										<button
											type='button'
											aria-label={
												showPassword
													? "Ocultar contraseña"
													: "Mostrar contraseña"
											}
											onClick={() =>
												setShowPassword((value) => !value)
											}
											className='absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
										>
											{showPassword ? (
												<EyeOff className='h-4 w-4' />
											) : (
												<Eye className='h-4 w-4' />
											)}
										</button>
									</div>
								</div>

								<button
									type='submit'
									disabled={isSubmitting}
									className='mt-2 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60'
								>
									Iniciar sesión
								</button>
							</form>
						)}
						<div className='mt-6 space-y-1 text-center text-xs text-slate-500'>
							{!isAdminMode ? (
								<p>
									¿No tienes cuenta?{" "}
									<Link
										to='/registro'
										className='text-primary hover:text-primary-hover'
									>
										Regístrate aquí
									</Link>
								</p>
							) : null}
							<p>
								<Link
									to='/forgot-password'
									className='cursor-pointer text-primary hover:text-primary-hover'
								>
									¿Olvidaste tu contraseña?
								</Link>
							</p>
							{isUnverifiedEmailError ? (
								<p>
									<button
										type='button'
										onClick={() => setShowResendModal(true)}
										className='cursor-pointer text-primary hover:text-primary-hover pt-1'
									>
										Verificar cuenta
									</button>
								</p>
							) : null}
						</div>
					</div>
				</div>
			</div>

			<ModalResendVerification
				open={showResendModal}
				onClose={() => setShowResendModal(false)}
				onEmailSent={() => {
				}}
			/>
			{/* ModalResetPassword solo debe abrirse desde el enlace de recuperación, no automáticamente */}
		</Layout>
	);
}
